import { apiClient } from '@/api/generated/client'
import type { AccountSearch, AccountUser, AccountWorkspace, PicksBySearch } from '@/api/generated/types'
import type { ClientConfig } from '@/api/generated/types'
import { StorageKeys, storageService } from '@/utils/localStorageService'
import { forgetAllDrafts } from '@/utils/spec/searchDraft'
import { syncsToServer, toSearchInput, type SearchRecord } from '@/utils/spec/searchHistory'

import {
  pushCampaigns,
  pushCompanies,
  pushLedger,
  pushPicks,
  pushPlan,
  pushSearch,
  pushSpec,
  pushWizard,
  setAccountSignedIn,
  type Stamped,
} from './accountPush'

/**
 * Браузер и сервер при входе: что куда переезжает.
 *
 * ОДИН РАЗ — перенос. Всё, что браузер копил до появления аккаунтов, уходит на сервер одним запросом.
 * Сервер принимает поиск, только если браузер предъявил его настоящий ключ: почта, введённая до входа,
 * не подтверждалась, и раздавать по ней поиски значило бы показать человеку чужие контакты.
 *
 * КАЖДЫЙ ВХОД — копия с сервера. Серверная запись ложится в localStorage, где её синхронно читают
 * сторы. Анкета и мастер сравниваются по отметке времени: более свежая запись браузера не затирается,
 * а досылается. Поиски, стадии и реестр сливаются.
 *
 * ЧЕЙ КАБИНЕТ В БРАУЗЕРЕ, записано отдельно (`accountOwner`). Вошёл другой человек — чужая копия
 * стирается, и в его кабинет не переносится ничего.
 */

/* Правки доски до 2026-10-12 жили в браузере под этим ключом; с варианта B правка — узел плана на сервере
   (plan/legacyEdits.ts переносит их при открытии доски). Здесь ключ только стирается при выходе. */
const EDITS_PREFIX = 'intch.workspace.edits.v1:'

/** Всё, что кабинет держит в браузере. Настройки консоли оператора и метки рекламы — не его. */
const ACCOUNT_KEYS = [
  StorageKeys.searches,
  StorageKeys.picks,
  StorageKeys.picksV2,
  StorageKeys.leadLedger,
  StorageKeys.replyLedger,
  StorageKeys.crm,
  StorageKeys.plan,
  StorageKeys.plgSpec,
  StorageKeys.plgApp,
  StorageKeys.plgAppAt,
  StorageKeys.order,
  StorageKeys.paidOrders,
  StorageKeys.companies,
  StorageKeys.companiesAt,
  StorageKeys.campaigns,
  StorageKeys.campaignsAt,
  StorageKeys.focus,
]

const localKeys = (): string[] => {
  try {
    return Object.keys(window.localStorage)
  } catch {
    return []
  }
}

/** Стереть копию кабинета: выход из аккаунта или вход другого человека. */
export const clearAccountCache = () => {
  for (const key of ACCOUNT_KEYS) storageService.removeItem(key)
  for (const key of localKeys()) if (key.startsWith(EDITS_PREFIX)) storageService.removeItem(key)
  /* Черновики окна поиска (utils/spec/searchDraft.ts) — по ключу на запись, не в списке выше. */
  forgetAllDrafts()
  storageService.removeItem(StorageKeys.accountOwner)
}

type LocalWizard = { runId?: number | null; token?: string | null } & Record<string, unknown>
type StoredSpec = { at: string; spec: Partial<ClientConfig> }

const localSearches = () => storageService.getItem<SearchRecord[]>(StorageKeys.searches) ?? []
const localWizard = () => storageService.getItem<LocalWizard>(StorageKeys.plgApp)
const localWizardAt = () => storageService.getItem<string>(StorageKeys.plgAppAt) ?? ''
const localSpec = () => storageService.getItem<StoredSpec>(StorageKeys.plgSpec)

const searchInput = toSearchInput

const hasLegacyData = () =>
  localSearches().length > 0 ||
  Boolean(storageService.getItem<unknown[]>(StorageKeys.companies)?.length) ||
  Boolean(storageService.getItem<unknown[]>(StorageKeys.campaigns)?.length) ||
  Boolean(localSpec()) ||
  Boolean(localWizard()?.runId) ||
  Boolean(storageService.getItem(StorageKeys.leadLedger)) ||
  localKeys().some((key) => key.startsWith(EDITS_PREFIX))

const stampedList = (key: string, atKey: string, name: 'companies' | 'campaigns') => {
  const list = storageService.getItem<unknown[]>(key)
  if (!Array.isArray(list) || !list.length) return {}
  return { [name]: { at: storageService.getItem<string>(atKey) || new Date(0).toISOString(), value: list } }
}

const importLegacy = async () => {
  const spec = localSpec()
  const wizard = localWizard()
  const plan = storageService.getItem<Record<string, unknown>>(StorageKeys.plan)
  const ledger = storageService.getItem<{ seen?: string[] }>(StorageKeys.leadLedger)
  await apiClient.me.importLocal({
    searches: localSearches().filter(syncsToServer).map(searchInput),
    picks: storageService.getItem<PicksBySearch>(StorageKeys.picksV2) ?? {},
    ledger: { seen: Array.isArray(ledger?.seen) ? ledger.seen.map(String) : [] },
    ...(spec ? { spec: { at: spec.at, value: spec.spec } } : {}),
    /* Имя и роль из контакта онбординга — в строку аккаунта (users.name/role), не в спеку: спека
       уезжает без контакта. Пусто — не шлём, чтобы не затереть то, что человек уже вписал в Profile. */
    ...(spec?.spec.contactName || spec?.spec.contactRole
      ? { profile: { name: String(spec.spec.contactName ?? ''), role: String(spec.spec.contactRole ?? '') } }
      : {}),
    ...(wizard ? { wizard: { at: localWizardAt() || new Date(0).toISOString(), value: wizard } } : {}),
    ...(plan ? { plan } : {}),
    ...stampedList(StorageKeys.companies, StorageKeys.companiesAt, 'companies'),
    ...stampedList(StorageKeys.campaigns, StorageKeys.campaignsAt, 'campaigns'),
  })
}

const stampOf = (value: unknown): Stamped<Record<string, unknown>> | null => {
  const record = value as Stamped<Record<string, unknown>> | null
  return record && typeof record === 'object' && record.value && typeof record.value === 'object' ? record : null
}

/** Серверная запись поиска → запись браузера. Ключ прогона остаётся тот, что уже был в браузере. */
const toRecord = (search: AccountSearch, token: string | null): SearchRecord => ({
  id: search.id,
  ...(search.name ? { name: search.name } : {}),
  runId: search.runId,
  token,
  growRunIds: search.growRunIds,
  at: search.at,
  qty: search.qty,
  total: search.total,
  currency: search.currency,
  poolY: search.poolY,
  host: search.host,
  rows: search.rows,
  spec: search.spec as Partial<ClientConfig>,
  source: search.source === 'import' ? 'purchase' : search.source,
  orderId: search.orderId,
  ...(search.funnel != null ? { funnel: search.funnel } : {}),
  paidAt: search.paidAt,
})

const hydrateList = (
  server: Stamped<Record<string, unknown>> | null,
  key: string,
  atKey: string,
  push: (stamped: Stamped<unknown[]>) => void,
) => {
  const local = storageService.getItem<unknown[]>(key)
  const localAt = storageService.getItem<string>(atKey) ?? ''
  const serverList = server && Array.isArray(server.value) ? (server.value as unknown[]) : null
  if (server && serverList && (!local || server.at > localAt)) {
    storageService.setItem(key, serverList)
    storageService.setItem(atKey, server.at)
  } else if (Array.isArray(local) && local.length && (!server || localAt > server.at)) {
    push({ at: localAt || new Date().toISOString(), value: local })
  }
}

const hydrate = (user: AccountUser, workspace: AccountWorkspace) => {
  /* Поиски: серверные — как есть, с ключом из браузера; только браузерные — досылаются. */
  const local = localSearches()
  const tokenById = new Map(local.map((record) => [record.id, record.token ?? null]))
  const serverIds = new Set(workspace.searches.map((search) => search.id))
  /* Покупка понарошку (utils/devUnlock.ts) на сервер не ездит, поэтому её запись в браузере главнее. */
  const localDev = new Map(local.filter((record) => record.localOrder).map((record) => [record.id, record]))
  const merged = workspace.searches.map((search) => localDev.get(search.id) ?? toRecord(search, tokenById.get(search.id) ?? null))
  for (const record of local) {
    if (serverIds.has(record.id)) continue
    merged.push(record)
    if (syncsToServer(record)) pushSearch(searchInput(record))
  }
  storageService.setItem(StorageKeys.searches, merged)

  /* Стадии: сервер, если у него есть; иначе браузерные уезжают туда. */
  const localPicks = storageService.getItem<PicksBySearch>(StorageKeys.picksV2) ?? {}
  const picks = Object.keys(workspace.picks).length ? (workspace.picks as PicksBySearch) : localPicks
  if (!Object.keys(workspace.picks).length && Object.keys(localPicks).length) pushPicks(localPicks)
  storageService.setItem(StorageKeys.picksV2, picks)
  storageService.setItem(
    StorageKeys.picks,
    Object.values(picks).flatMap((entries) => entries.map((entry) => entry.id)),
  )

  /* Реестр — объединением в обе стороны: выданного человека не забывает ни одна из копий. */
  const localSeen = (storageService.getItem<{ seen?: string[] }>(StorageKeys.leadLedger)?.seen ?? []).map(String)
  const serverSeen = new Set(workspace.ledger.seen)
  const missing = localSeen.filter((id) => !serverSeen.has(id))
  if (missing.length) pushLedger(missing)
  storageService.setItem(StorageKeys.leadLedger, { seen: [...workspace.ledger.seen, ...missing] })

  /* Пакет: серверный, если есть. */
  const localPlan = storageService.getItem<Record<string, unknown>>(StorageKeys.plan)
  if (user.plan) storageService.setItem(StorageKeys.plan, user.plan)
  else if (localPlan) pushPlan(localPlan)

  /* Анкета: свежая запись побеждает. Контакт — всегда из аккаунта: он подтверждён кодом. */
  const contact: Partial<ClientConfig> = {
    ...(user.email ? { contactEmail: user.email } : {}),
    ...(user.name ? { contactName: user.name } : {}),
    ...(user.role ? { contactRole: user.role } : {}),
  }
  const serverSpec = stampOf(user.spec)
  const spec = localSpec()
  if (serverSpec && (!spec || serverSpec.at > spec.at)) {
    storageService.setItem(StorageKeys.plgSpec, { at: serverSpec.at, spec: { ...(serverSpec.value as Partial<ClientConfig>), ...contact } })
  } else if (spec) {
    storageService.setItem(StorageKeys.plgSpec, { at: spec.at, spec: { ...spec.spec, ...contact } })
    if (!serverSpec || spec.at > serverSpec.at) {
      const { contactEmail: _e, contactName: _n, contactRole: _r, ...rest } = spec.spec
      pushSpec({ at: spec.at, value: rest })
    }
  }

  /* Компании и кампании: списки целиком, свежая запись побеждает. */
  hydrateList(stampOf(user.companies), StorageKeys.companies, StorageKeys.companiesAt, pushCompanies)
  hydrateList(stampOf(user.campaigns), StorageKeys.campaigns, StorageKeys.campaignsAt, pushCampaigns)

  /* Мастер: так же. Ключ прогона берётся из браузера, если прогон тот же. */
  const serverWizard = stampOf(user.wizard)
  const wizard = localWizard()
  const wizardAt = localWizardAt()
  if (serverWizard && (!wizard || serverWizard.at > wizardAt)) {
    const token = wizard?.runId === serverWizard.value.runId ? (wizard?.token ?? null) : null
    storageService.setItem(StorageKeys.plgApp, { ...serverWizard.value, token })
    storageService.setItem(StorageKeys.plgAppAt, serverWizard.at)
  } else if (wizard && (!serverWizard || wizardAt > serverWizard.at)) {
    pushWizard({ at: wizardAt || new Date().toISOString(), value: wizard })
  }
}

/**
 * Человек вошёл (или открыл приложение уже вошедшим). Порядок важен: сначала решить, чья копия лежит
 * в браузере, потом перенести, потом заполнить копию с сервера, и только потом разрешить записи.
 */
export const syncAfterSignIn = async (user: AccountUser) => {
  const owner = storageService.getItem<string>(StorageKeys.accountOwner)
  if (owner && owner !== user.intch_user_id) clearAccountCache()
  else if (!owner && hasLegacyData()) {
    try {
      await importLegacy()
    } catch {
      /* Не вышло сейчас — выйдет при следующем входе: владелец не записан, копия цела. */
      setAccountSignedIn(true)
      return
    }
  }

  const workspace = await apiClient.me.workspace()
  /* Записи разрешаются ДО заполнения: то, что есть только в браузере, заполнение тут же досылает. */
  setAccountSignedIn(true)
  hydrate(user, workspace)
  storageService.setItem(StorageKeys.accountOwner, user.intch_user_id)
}

export const markSignedOut = () => {
  setAccountSignedIn(false)
  clearAccountCache()
}

/**
 * Вход кончился сам — монолит отверг refresh, человек из аккаунта не выходил. Копия остаётся: пуши
 * на сервер молчат о своих сбоях (accountPush.ts), и то, что не доехало, живёт только здесь. При
 * следующем входе того же человека syncAfterSignIn досылает её; другого — стирает по `accountOwner`.
 */
export const markSessionLost = () => {
  setAccountSignedIn(false)
}
